import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowBack,
  Edit,
  Save,
  Timelapse,
} from '@material-ui/icons';
import {
  Checkbox, FormControlLabel, FormGroup, Grid,
} from '@material-ui/core';
import {
  Fab,
  GlobalActions,
  Panel,
  PreventTransitionPrompt,
  useDialogs,
} from '@michelin/acid-components';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { saveCommercialProducts } from 'hooks/useCommercialProducts';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { commercialProductFieldDetail, commercialProductFieldLabel, CommercialProducts } from './CommercialProductsFields';
import { useSnackbar } from 'notistack';

interface Props {
  products: CommercialProducts,
  action: 'edit' | 'view' | 'add',
}

interface CheckBoxItem {
  key: string,
  disabled?: boolean,
  enabledIf?: string[],
  onChange?: (data: CommercialProducts) => void,
}

function isItemDisabled(item:CheckBoxItem, values:CommercialProducts) {
  if (item.disabled) return true;
  if (!item.enabledIf) return false;
  return !item.enabledIf.some((key) => (values as any)[key]);
}

const commercialTruckProducts:CheckBoxItem[] = [
  { key: 'michelin_new_tires', disabled: true },
  { key: 'x_one_tires', enabledIf: ['michelin_new_tires'] },
  { key: 'bfgoodrich_tires', disabled: true },
  { key: 'uniroyal_tires', disabled: true },
  { key: 'truck_associate_dealer', disabled: true },
  { key: 'travel_plaza', disabled: true },
  { key: 'mrt_dealer' },
  { key: 'mrt_associate_dealer', disabled: true },
  { key: 'oliver_retreads' },
  { key: 'megamile_retreads' },
  { key: 'ultra_low_profile_wide_design_retreads', enabledIf: ['oliver_retreads', 'megamile_retreads'] },
];

const earthmoverProducts:CheckBoxItem[] = [
  { key: 'material_handling', disabled: true },
  { key: 'off_the_road', disabled: true },
  { key: 'small_earthmover', disabled: true },
  { key: 'large_material_handling', disabled: true },
  { key: 'giant_earthmover', disabled: true },
];

const agriculturalProducts:CheckBoxItem[] = [
  { key: 'authorized_michelin_ag_tires_supplier', disabled: true },
  { key: 'r4_compact_line', disabled: true },
  { key: 'key_associate_dealer', disabled: true },
];

function CommercialPanel(props: Props) {
  const { t } = useTranslation();
  const { stNumber, btNumber } = useParams<{ stNumber: string, btNumber: string }>();
  const history = useHistory();
  const permissions = usePermissionsSPP();
  const {
    control, formState, getValues, setValue, reset,
  } = useForm({ defaultValues: props.products });
  const queryClient = useQueryClient();
  const saveMutation = useMutation(saveCommercialProducts, {
    onSuccess: () => queryClient.invalidateQueries(['commercial-products', stNumber]),
  });
  const { errorDialog } = useDialogs();
  const { enqueueSnackbar } = useSnackbar();
  const readOnly: boolean = props.action === 'view';
  const [renders, setRenders] = useState(1);
  const forceRerender = () => setRenders(renders + 1);

  const onRetreadsChange = (data:CommercialProducts) => {
    if (!data.oliver_retreads && !data.megamile_retreads && data.ultra_low_profile_wide_design_retreads) {
      setValue('ultra_low_profile_wide_design_retreads', false);
      forceRerender();
    }
  };

  const oliver_retreads = commercialTruckProducts.find((cp) => cp.key === 'oliver_retreads');
  const megamile_retreads = commercialTruckProducts.find((cp) => cp.key === 'megamile_retreads');
  if (oliver_retreads) oliver_retreads.onChange = onRetreadsChange;
  if (megamile_retreads) megamile_retreads.onChange = onRetreadsChange;

  useEffect(() => {
    if (props.action === 'view') reset();
  }, [props.action, reset]);

  useEffect(() => {
    reset(props.products);
  }, [props.products, reset]);

  const save = async () => {
    try {
      if (!formState.isDirty) return;
      const data = { ...getValues() };
      // @ts-ignore
      delete data.customer_type;
      await saveMutation.mutateAsync({ stNumber, data });
      reset();
      history.push('view');
      enqueueSnackbar(t('Commercial Products information saved.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    } catch (error) {
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Commercial Products Data.'),
      );
    }
  };

  const renderActionButtons = () => {
    if (!permissions.allowsAction('location_information.update')) {
      if (!readOnly) history.push('view');
      return null;
    }

    return (
      <GlobalActions>
        <Fab
          color="primary"
          aria-label={t('Edit')}
          onClick={() => history.push(`/${btNumber}/commercial/${stNumber}/products/edit`)}
          disabled={!readOnly}
        >
          <Edit />
        </Fab>
        <Fab
          color="inherit"
          aria-label={t('Saving')}
          disabled
          displayDisabled={saveMutation.isLoading}
        >
          <Timelapse />
        </Fab>
        <Fab
          color={formState.isDirty ? 'primary' : 'inherit'}
          aria-label={t('Save')}
          onClick={save}
          disabled={saveMutation.isLoading || readOnly}
        >
          <Save />
        </Fab>
        <Fab
          color="primary"
          aria-label={t('Back')}
          onClick={() => {
            history.push(`/${btNumber}/commercial/${stNumber}/products/view`);
          }}
          disabled={saveMutation.isLoading || readOnly}
          displayDisabled={!readOnly}
        >
          <ArrowBack />
        </Fab>
      </GlobalActions>
    );
  };

  const createGroupSelector = (fields: CheckBoxItem[]) => {
    let checked: number = 0;
    let unchecked: number = 0;
    const values:any = getValues();
    fields.forEach((field) => {
      if (isItemDisabled(field, values)) return;
      if (!values[field.key]) unchecked++;
      else checked++;
    });
    if (checked === 0 && unchecked === 0) return null;
    return (
      <Checkbox
        checked={unchecked === 0}
        indeterminate={checked > 0 && unchecked > 0}
        disabled={readOnly}
        readOnly={readOnly}
        color="primary"
        onChange={(e, v) => {
          fields.forEach((field) => {
            if (isItemDisabled(field, values) && field.key !== 'ultra_low_profile_wide_design_retreads') return;
            setValue(field.key as any, v, { shouldDirty: true });
            if (field.onChange) field.onChange(getValues());
          });
          forceRerender();
        }}
      />
    );
  };

  const renderCheckboxGroup = (title:string, items:CheckBoxItem[], cols:4|6|12) => {
    const groupSelector = createGroupSelector(items);
    const values = getValues();

    return (
      <Panel title={t(title)}>
        <FormGroup>
          {!readOnly && groupSelector
            ? (
              <Grid container spacing={2} style={{ paddingLeft: 7 }}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel control={groupSelector} label={t('Select All')} />
                </Grid>
              </Grid>
            ) : null}
          <Grid container spacing={2} style={{ paddingLeft: 35 }}>
            {items.map((item) => (
              <Grid item xs={12} md={cols} key={item.key}>
                <FormControlLabel
                  control={(
                    <Controller
                      name={item.key as any}
                      control={control}
                      rules={{ required: true }}
                      render={(props) => (
                        <Checkbox
                          id={item.key}
                          checked={props.field.value}
                          onChange={(e) => {
                            if (readOnly) return;
                            props.field.onChange(e.target.checked);
                            if (item.onChange) item.onChange(getValues());
                            forceRerender();
                          }}
                          disabled={!readOnly && isItemDisabled(item, values)}
                          readOnly={readOnly}
                          color="primary"
                        />
                      )}
                    />
                    )}
                  label={
                    commercialProductFieldDetail(item.key)
                      ? (
                        <>
                          <span style={{ fontWeight: 'bold' }}>{t(commercialProductFieldLabel(item.key))}</span>
                          {` ${t(commercialProductFieldDetail(item.key) || '')}`}
                        </>
                      )
                      : t(commercialProductFieldLabel(item.key))
                  }
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </Panel>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        {renderActionButtons()}
        <Grid item xs={12} md={12}>
          {renderCheckboxGroup('Commercial Truck Products', commercialTruckProducts, 6)}
        </Grid>
        <Grid item xs={12} md={12}>
          {renderCheckboxGroup('Earthmover Products', earthmoverProducts, 12)}
        </Grid>
        <Grid item xs={12} md={12}>
          {renderCheckboxGroup('Agricultural Products', agriculturalProducts, 6)}
        </Grid>
      </Grid>
      <PreventTransitionPrompt
        when={formState.isDirty}
      />
    </>
  );
}

export default CommercialPanel;
